import {
  createDirectus,
  rest,
  staticToken,
  readMe,
  authentication,
  passwordRequest,
  readItems,
  readItem,
  updateItem,
  createItem,
  deleteItem,
  readUsers,
  readRoles,
  updateUser,
  createUser,
  deleteUser,
  updateRole,
  createRole,
  deleteRole,
} from '@directus/sdk';
import { boot } from 'quasar/wrappers';

export default boot(async ({ app, store, router }) => {
  const token = localStorage.getItem('greeners_auth_token');

  let directus = createDirectus(process.env.GREENERS_URL)
    .with(rest());

  if (token) {
    directus = directus.with(staticToken(token));
  }

  if (token) {
    try {
      await directus.request(readMe());
    } catch (error) {
      localStorage.removeItem('greeners_auth_token');

      store.commit('user/cu', {
        id: '',
        first_name: '',
        last_name: '',
        email: '',
        role: '',
        status: '',
        pharmacy_token: '',
      });

      store.commit('user/greeners', {
        id: '',
        first_name: '',
        last_name: '',
        email: '',
        role: '',
        status: '',
      });

      await router.push({
        path: '/auth/login',
        query: { removeToken: true },
      });
    }
  }

  const GREENERS_CORE_COLLECTIONS = [
    'users',
    'directus_users',
    'directus_roles',
  ];

  const getDirectusInstance = () => directus;

  const createCollectionAccessor = (collection) => {
    const isCoreCollection = GREENERS_CORE_COLLECTIONS.includes(collection);
    
    return {
      readByQuery: async (query = {}) => {
        let result;
        if (isCoreCollection) {
          const currentInstance = getDirectusInstance();
          if (collection === 'users' || collection === 'directus_users') {
            result = await currentInstance.request(readUsers(query));
          } else if (collection === 'directus_roles') {
            result = await currentInstance.request(readRoles(query));
          } else {
            result = await currentInstance.request(readItems(collection, query));
          }
        } else {
          const currentInstance = getDirectusInstance();
          result = await currentInstance.request(readItems(collection, query));
        }
        
        if (result && !result.data && Array.isArray(result)) {
          return { data: result };
        } else if (result && result.data) {
          return result;
        } else if (result) {
          return { data: result };
        }
        return { data: [] };
      },
      readOne: async (id, options = {}) => {
        const currentInstance = getDirectusInstance();
        if (isCoreCollection) {
          if (collection === 'users' || collection === 'directus_users') {
            const query = {
              filter: {
                id: {
                  _eq: id,
                },
              },
              ...options,
            };
            const result = await currentInstance.request(readUsers(query));
            return result?.data?.[0] ?? result?.[0] ?? null;
          }
          if (collection === 'directus_roles') {
            const query = {
              filter: {
                id: {
                  _eq: id,
                },
              },
              ...options,
            };
            const result = await currentInstance.request(readRoles(query));
            return result?.data?.[0] ?? result?.[0] ?? null;
          }
        }
        const response = await currentInstance.request(readItem(collection, id, options));
        return response?.data ?? response;
      },
      updateOne: async (id, body) => {
        const currentInstance = getDirectusInstance();
        if (isCoreCollection) {
          if (collection === 'users' || collection === 'directus_users') {
            return await currentInstance.request(updateUser(id, body));
          }
          if (collection === 'directus_roles') {
            return await currentInstance.request(updateRole(id, body));
          }
        }
        return await currentInstance.request(updateItem(collection, id, body));
      },
      createOne: async (body) => {
        const currentInstance = getDirectusInstance();
        if (isCoreCollection) {
          if (collection === 'users' || collection === 'directus_users') {
            return await currentInstance.request(createUser(body));
          }
          if (collection === 'directus_roles') {
            return await currentInstance.request(createRole(body));
          }
        }
        return await currentInstance.request(createItem(collection, body));
      },
      deleteOne: async (id) => {
        const currentInstance = getDirectusInstance();
        if (isCoreCollection) {
          if (collection === 'users' || collection === 'directus_users') {
            return await currentInstance.request(deleteUser(id));
          }
          if (collection === 'directus_roles') {
            return await currentInstance.request(deleteRole(id));
          }
        }
        return await currentInstance.request(deleteItem(collection, id));
      },
    };
  };

  const greenersWrapper = {
    url: process.env.GREENERS_URL,
    request: async (...args) => {
      const currentInstance = getDirectusInstance();
      return await currentInstance.request(...args);
    },
    
    login: async (credentials) => {
      const { email, password } = credentials;
      const loginClient = createDirectus(process.env.GREENERS_URL)
        .with(rest())
        .with(authentication('json'));
      return await loginClient.login({ email, password });
    },
    
    passwordRequest: async (email, options = {}) => {
      const currentInstance = getDirectusInstance();
      return await currentInstance.request(passwordRequest(email, options));
    },
    
    auth: {
      static: async (token) => {
        if (token) {
          localStorage.setItem('greeners_auth_token', token);
        } else {
          localStorage.removeItem('greeners_auth_token');
        }
        
        let newDirectus = createDirectus(process.env.GREENERS_URL)
          .with(rest());
        
        if (token) {
          newDirectus = newDirectus.with(staticToken(token));
        }
        
        directus = newDirectus;
      }
    },
    
    users: {
      ...createCollectionAccessor('users'),
      me: {
        read: async () => {
          const currentInstance = getDirectusInstance();
          return await currentInstance.request(readMe());
        }
      }
    },
    
    items: (collection) => {
      return createCollectionAccessor(collection);
    }
  };

  app.config.globalProperties.$greeners = greenersWrapper;
  
  if (typeof window !== 'undefined') {
    window.$greeners = greenersWrapper;
  }
});
